
import { createContext, useContext, useEffect } from "react";
import { useAddItemState } from "./state";
import { useAddItemOperations } from "./operations";
import { 
  AddItemContextType, 
  AddItemProviderProps 
} from "./types";
import { toast } from "sonner";

// Create context with undefined as initial value
const AddItemContext = createContext<AddItemContextType | undefined>(undefined);

export const useAddItem = () => {
  const context = useContext(AddItemContext);
  if (!context) {
    throw new Error("useAddItem must be used within an AddItemProvider");
  }
  return context;
};

export const AddItemProvider = ({
  children,
  open,
  onOpenChange,
  onAddItem,
  onItemAdded
}: AddItemProviderProps) => {
  // Get all state management functions and values
  const state = useAddItemState({ open, onOpenChange });
  
  // Get all operations for the add item form
  const operations = useAddItemOperations({
    ...state,
    onOpenChange,
    onAddItem,
    onItemAdded
  });
  
  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      state.setIsOffline(false);
      toast.success("You're back online!");
    };
    
    const handleOffline = () => {
      state.setIsOffline(true);
      toast.warning("You're offline. Items will be saved locally.");
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [state]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        state.setFormData({
          name: "",
          quantity: 1,
          category: "",
          expiration: new Date(),
          location: "",
          notes: "",
          image_url: "",
        });
        state.setActiveTab("manual");
        state.setErrors({
          name: "",
          quantity: "",
          category: "",
          expiration: "",
        });
        state.setIsDuplicate(false);
        state.setPendingItems([]);
      }, 300);
    }
  }, [open, state]);

  // Combine all context values
  const value: AddItemContextType = {
    ...state,
    ...operations,
  };

  return (
    <AddItemContext.Provider value={value}>
      {children}
    </AddItemContext.Provider>
  );
};

export default AddItemProvider;
